import React, {
  useRef, useEffect, forwardRef, useImperativeHandle,
  useState, useCallback, useMemo,
} from "react";
import NounCandidatePopover from "./NounCandidatePopover";
import { MentionDropdown } from "./MentionDropdown";

/**
 * NounAwareTextArea
 *
 * The textarea is always fully visible and editable — its text is never hidden.
 * A pointer-events:none overlay div sits on top and renders coloured <mark> backgrounds
 * that align with the detected noun spans. The overlay text is transparent; only the
 * mark backgrounds and border-bottom are visible, so the user sees coloured bands
 * under their own text without any double-rendering or font-mismatch flicker.
 *
 * Popover hover is driven by onMouseMove on the textarea itself, hit-testing each
 * mark element's getBoundingClientRect(). No hotspot spans or z-index tricks needed.
 *
 * @mention mode: when the user types @+2 chars a dropdown is shown with entity
 * suggestions. Selecting an entry replaces the @query with the entity text.
 * Scan is suppressed while @mention mode is active.
 */
const NounAwareTextArea = forwardRef(function NounAwareTextArea(
  {
    classNames,
    rowsCount,
    value,
    onChangeEvent,
    placeholderLabel,
    onStartedTyping,
    disabledState,
    requiredField,
    onKeyDown: externalKeyDown,
    detections = [],
    onReplace,
    rootOU,
    // @mention props
    mentionSuggestions = [],
    isMentionLoading   = false,
    onMentionQueryChange,
    onMentionApply,
  },
  ref
) {
  const textareaRef   = useRef(null);
  const mirrorRef     = useRef(null);
  const markRefs      = useRef([]);
  const mentionAncRef = useRef(null);  // anchor span in overlay for dropdown positioning
  const hideTimerRef  = useRef(null);

  const [activeDetection, setActiveDetection]   = useState(null);
  const [popoverPos, setPopoverPos]             = useState({ top: 0, left: 0 });
  const [cursorPos, setCursorPos]               = useState(0);
  const [mentionActiveIdx, setMentionActiveIdx] = useState(-1);

  useImperativeHandle(ref, () => textareaRef.current);

  // ── Active @mention detection ──────────────────────────────────────────────
  // Matches /@(firstWord secondWord?)$/ on the text up to the cursor.
  // Rules:
  //   - At most ONE space is allowed: "@john s" is valid, "@john s " deactivates.
  //   - A second space means the user has finished the entity token and is
  //     continuing normal free-text input — mention mode closes immediately.
  //   - Ignored when '@' is preceded by a word character (e.g. john@doe.com).
  const activeMention = useMemo(() => {
    if (!value) return null;
    const textBefore = value.slice(0, cursorPos);
    // [^\s@]+          — first word (required, no spaces)
    // (\s[^\s@]*)?     — optional: one space + optional second word fragment
    // $                — must be at end of text-before-cursor
    // A second space fails the match → returns null → closes dropdown.
    const match = textBefore.match(/@([^\s@]+(\s[^\s@]*)?)$/);
    if (!match) return null;
    const query = match[1].trimEnd(); // drop trailing space while user pauses between words
    if (query.length < 2) return null;
    // If the character immediately before '@' is a word char, it's an email — skip.
    const atPos = cursorPos - match[0].length;
    if (atPos > 0 && /\w/.test(value[atPos - 1])) return null;
    return {
      query,
      atStart: atPos,   // index of '@' in value
      atEnd:   cursorPos,
    };
  }, [value, cursorPos]);

  // Notify parent when activeMention changes so it can pause/resume scan.
  useEffect(() => {
    onMentionQueryChange?.(activeMention?.query ?? null);
  }, [activeMention, onMentionQueryChange]);

  // Reset keyboard index whenever the suggestion list changes.
  useEffect(() => { setMentionActiveIdx(-1); }, [mentionSuggestions]);

  // ── Auto-grow ──────────────────────────────────────────────────────────────
  const autoGrow = (el) => {
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };
  useEffect(() => {
    if (textareaRef.current) autoGrow(textareaRef.current);
  }, [value]);

  // ── Sync mirror scroll ─────────────────────────────────────────────────────
  const handleScroll = () => {
    if (mirrorRef.current && textareaRef.current) {
      mirrorRef.current.scrollTop  = textareaRef.current.scrollTop;
      mirrorRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  // ── Sync computed styles from textarea → mirror ────────────────────────────
  useEffect(() => {
    if (!textareaRef.current || !mirrorRef.current) return;
    const cs = window.getComputedStyle(textareaRef.current);
    const m  = mirrorRef.current;
    m.style.paddingTop    = cs.paddingTop;
    m.style.paddingRight  = cs.paddingRight;
    m.style.paddingBottom = cs.paddingBottom;
    m.style.paddingLeft   = cs.paddingLeft;
    m.style.fontFamily    = cs.fontFamily;
    m.style.fontSize      = cs.fontSize;
    m.style.fontWeight    = cs.fontWeight;
    m.style.lineHeight    = cs.lineHeight;
    m.style.letterSpacing = cs.letterSpacing;
    m.style.wordSpacing   = cs.wordSpacing;
    m.style.borderTopWidth    = cs.borderTopWidth;
    m.style.borderRightWidth  = cs.borderRightWidth;
    m.style.borderBottomWidth = cs.borderBottomWidth;
    m.style.borderLeftWidth   = cs.borderLeftWidth;
  }, []);

  // ── Sorted, bounds-checked detections ─────────────────────────────────────
  const sortedDetections = useMemo(() => {
    if (!detections.length) return [];
    const text = value ?? "";
    return [...detections]
      .map((d) => {
        if (!d.span) return d;
        if (text.slice(d.startIndex, d.endIndex) === d.span) return d;
        const searchFrom = Math.max(0, d.startIndex - 5);
        const found = text.indexOf(d.span, searchFrom);
        if (found !== -1 && found <= d.startIndex + 5) {
          return { ...d, startIndex: found, endIndex: found + d.span.length };
        }
        return d;
      })
      .filter(
        (d) =>
          d.startIndex >= 0 &&
          d.endIndex   <= text.length &&
          d.startIndex <  d.endIndex
      )
      .sort((a, b) => a.startIndex - b.startIndex);
  }, [detections, value]);

  // Clear popover when a new scan result arrives
  useEffect(() => { setActiveDetection(null); }, [detections]);

  // ── Popover show / hide ────────────────────────────────────────────────────
  const cancelHide = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  }, []);

  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setActiveDetection(null), 400);
  }, []);

  // Global mousemove listener while a popover is open.
  useEffect(() => {
    if (!activeDetection) return;

    const activeIdx = sortedDetections.indexOf(activeDetection);
    const PADDING = 6;

    const handleGlobalMove = (e) => {
      const { clientX, clientY } = e;

      const markEl = markRefs.current[activeIdx];
      if (markEl) {
        const r = markEl.getBoundingClientRect();
        if (
          clientX >= r.left  - PADDING && clientX <= r.right  + PADDING &&
          clientY >= r.top   - PADDING && clientY <= r.bottom + PADDING
        ) {
          cancelHide();
          return;
        }
      }

      const els = document.elementsFromPoint(clientX, clientY);
      if (els.some((el) => el.closest("[data-noun-popover]"))) {
        cancelHide();
        return;
      }

      scheduleHide();
    };

    document.addEventListener("mousemove", handleGlobalMove);
    return () => document.removeEventListener("mousemove", handleGlobalMove);
  }, [activeDetection, sortedDetections, cancelHide, scheduleHide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute smart popover position: flip above the mark when space below is insufficient.
  const calcPopoverPos = useCallback((rect, candidateCount) => {
    const HEADER_H    = 28;   // label row height (px)
    const ITEM_H      = 34;   // per-candidate row height (px)
    const PADDING_V   = 16;   // top+bottom py-2 padding (px)
    const GAP         = 4;    // gap between mark and popover (px)
    const MIN_W       = 180;  // min-w-[180px] from className

    const estimatedH  = HEADER_H + candidateCount * ITEM_H + PADDING_V;
    const spaceBelow  = window.innerHeight - rect.bottom;
    const top         = spaceBelow >= estimatedH + GAP
      ? rect.bottom + GAP                   // enough room — place below
      : rect.top - estimatedH - GAP;        // not enough room — flip above

    // Clamp left so popover doesn't overflow the right edge
    const left = Math.min(rect.left, window.innerWidth - MIN_W - 8);

    return { top, left };
  }, []);

  // Hit-test mouse position against each mark's bounding rect.
  const handleMouseMove = useCallback(
    (e) => {
      const { clientX, clientY } = e;
      for (let i = 0; i < sortedDetections.length; i++) {
        const el = markRefs.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (
          clientX >= rect.left && clientX <= rect.right &&
          clientY >= rect.top  && clientY <= rect.bottom
        ) {
          cancelHide();
          if (activeDetection !== sortedDetections[i]) {
            const candidateCount = sortedDetections[i]?.candidates?.length ?? 3;
            setPopoverPos(calcPopoverPos(rect, candidateCount));
            setActiveDetection(sortedDetections[i]);
          }
          return;
        }
      }
    },
    [sortedDetections, activeDetection, cancelHide, calcPopoverPos]
  );

  // ── @mention keyboard/selection handling ──────────────────────────────────
  const handleMentionSelect = useCallback(
    (suggestion) => {
      if (!activeMention || !textareaRef.current) return;
      const { atStart, atEnd } = activeMention;
      const before  = value.slice(0, atStart);
      const after   = value.slice(atEnd);
      const newText = before + suggestion.text + after;
      onMentionApply?.(newText);
    },
    [activeMention, value, onMentionApply]
  );

  const handleKeyDown = useCallback(
    (e) => {
      const mentionOpen = activeMention && (isMentionLoading || mentionSuggestions.length > 0);
      if (mentionOpen) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionActiveIdx((i) => Math.min(i + 1, mentionSuggestions.length - 1));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionActiveIdx((i) => Math.max(i - 1, -1));
          return;
        }
        if (e.key === "Enter" && mentionActiveIdx >= 0) {
          e.preventDefault();
          handleMentionSelect(mentionSuggestions[mentionActiveIdx]);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          onMentionQueryChange?.(null);
          return;
        }
      }
      externalKeyDown?.(e);
    },
    [activeMention, isMentionLoading, mentionSuggestions, mentionActiveIdx, handleMentionSelect, externalKeyDown, onMentionQueryChange]
  );

  // ── Build segments (detections + optional @mention span) ──────────────────
  const segments = buildSegments(value ?? "", sortedDetections, activeMention);

  return (
    <div className="relative">
      {/* ── Mirror overlay — renders all visible text + marks ─────────────── */}
      {/* The textarea text is transparent (see below) so spellcheck underlines
          are invisible. This overlay renders the actual visible text, which is
          NOT subject to browser spellcheck — so detected words show only our
          purple underline, not the browser's red wavy one. */}
      <div
        ref={mirrorRef}
        aria-hidden="true"
        className="absolute inset-0 overflow-hidden pointer-events-none whitespace-pre-wrap break-words"
        style={{
          // Text is visible here — this is what the user reads.
          // color is intentionally unset so it inherits the textarea's text colour.
          boxSizing: "border-box",
          maxHeight: "calc(1.5em * 5)",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          borderStyle: "solid",
          borderColor: "transparent",
        }}
      >
        {segments.map((seg, i) => {
          if (seg.isMention) {
            return (
              <span
                key={i}
                ref={mentionAncRef}
                style={{ background: "#F3F1FF", borderRadius: 2 }}
              >
                {seg.text}
              </span>
            );
          }
          if (seg.detection) {
            const dIdx = sortedDetections.indexOf(seg.detection);
            return (
              <mark
                key={i}
                ref={(el) => { markRefs.current[dIdx] = el; }}
                style={{
                  // Text stays visible (inherits color) — only the background
                  // and underline are added. No color: transparent here so the
                  // user still reads the word normally under our highlight.
                  background:   "#F3F1FF",
                  borderBottom: "2px solid #5E62CC",
                  borderRadius: "2px",
                }}
              >
                {seg.text}
              </mark>
            );
          }
          return <span key={i}>{seg.text}</span>;
        })}
      </div>

      {/* ── Textarea — editable but text is transparent ────────────────────── */}
      {/* Making the text transparent hides the browser's spellcheck underlines
          (they are rendered on the textarea's own text layer).
          caretColor keeps the blinking cursor visible.
          spellCheck={false} is safe here since the text is invisible anyway —
          spellcheck on invisible text would be useless and wasteful. */}
      <textarea
        ref={textareaRef}
        rows={rowsCount}
        value={value}
        onChange={(e) => {
          setCursorPos(e.target.selectionStart ?? 0);
          onChangeEvent(e);
          if (onStartedTyping) onStartedTyping(true);
        }}
        onSelect={(e) => setCursorPos(e.target.selectionStart ?? 0)}
        onClick={(e)  => setCursorPos(e.target.selectionStart ?? 0)}
        onKeyUp={(e)  => setCursorPos(e.target.selectionStart ?? 0)}
        onKeyDown={handleKeyDown}
        onScroll={handleScroll}
        placeholder={placeholderLabel}
        onInput={(e) => autoGrow(e.target)}
        onMouseMove={handleMouseMove}
        spellCheck={false}
        style={{
          maxHeight:   "calc(1.5em * 5)",
          lineHeight:  "1.5em",
          background:  "transparent",
          position:    "relative",
          color:       "transparent",   // hide text → hides spellcheck underlines
          caretColor:  "black",         // but keep the cursor visible
        }}
        className={classNames}
        maxLength={2000}
        disabled={disabledState}
        required={requiredField}
      />

      {/* ── Candidate popover ─────────────────────────────────────────────── */}
      {activeDetection && (
        <NounCandidatePopover
          detection={activeDetection}
          position={popoverPos}
          onClose={() => setActiveDetection(null)}
          currentText={value}
          onReplace={onReplace}
          rootOU={rootOU}
        />
      )}

      {/* ── @mention dropdown ─────────────────────────────────────────────── */}
      {activeMention && (isMentionLoading || mentionSuggestions.length > 0) && (
        <MentionDropdown
          anchorEl={mentionAncRef.current}
          suggestions={mentionSuggestions}
          isLoading={isMentionLoading}
          activeIndex={mentionActiveIdx}
          onSelect={handleMentionSelect}
          onDismiss={() => onMentionQueryChange?.(null)}
        />
      )}
    </div>
  );
});

/**
 * Splits `text` into plain-text and detected-span segments.
 * Receives pre-sorted, bounds-checked detections.
 * If `activeMention` is set, the @query range is emitted as an `isMention` segment.
 */
function buildSegments(text, sortedDetections, activeMention) {
  // Build a combined list of ranges to render, sorted by startIndex.
  // @mention takes priority: detection ranges overlapping the @query are dropped.
  const ranges = [];

  if (activeMention) {
    const { atStart, atEnd } = activeMention;
    ranges.push({ start: atStart, end: atEnd, detection: null, isMention: true });
    // Add detections only if they don't overlap the @mention range.
    for (const d of sortedDetections) {
      if (d.endIndex <= atStart || d.startIndex >= atEnd) {
        ranges.push({ start: d.startIndex, end: d.endIndex, detection: d, isMention: false });
      }
    }
  } else {
    for (const d of sortedDetections) {
      ranges.push({ start: d.startIndex, end: d.endIndex, detection: d, isMention: false });
    }
  }

  ranges.sort((a, b) => a.start - b.start);

  const segments = [];
  let cursor = 0;
  for (const r of ranges) {
    if (r.start < cursor) continue;
    if (r.start > cursor) segments.push({ text: text.slice(cursor, r.start), detection: null, isMention: false });
    segments.push({ text: text.slice(r.start, r.end), detection: r.detection, isMention: r.isMention });
    cursor = r.end;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), detection: null, isMention: false });
  return segments;
}

export default NounAwareTextArea;
